import AXIOS from 'axios'
import { useTokenStore } from '@/pinia/stores/tokenStore'
import { useUserStore } from '@/pinia/stores/userStore'
import { useClusterStore } from '@/pinia/stores/clusterStore'
import pinia from '@/pinia'

// 세션 만료 시간(ms) - 기본 30분
const LOGIN_EXPIRY_DURATION_MS = 30 * 60 * 1000

const guardHandled = (result) => result

const guardSkip = () => undefined

const restoreClusterFromStorage = () => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem('pinia_cluster')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const cluster = parsed?.clusterInfo
    return cluster && cluster.id ? cluster : null
  } catch (error) {
    return null
  }
}

const getFrontendType = () => {
  const runtimeConfig =
    typeof globalThis === 'object' ? globalThis.__APP_CONFIG__ : null
  if (runtimeConfig && runtimeConfig.VITE_FRONTEND_TYPE) {
    return String(runtimeConfig.VITE_FRONTEND_TYPE)
  }
  if (typeof ENV !== 'undefined' && ENV && ENV.FRONTEND_TYPE) {
    return String(ENV.FRONTEND_TYPE)
  }
  return undefined
}

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    if (pad) base64 += '='.repeat(4 - pad)
    return JSON.parse(atob(base64))
  } catch (error) {
    return null
  }
}

/**
 * 테스트 모드일 때 특정 경로로 강제 리다이렉트
 * @param {import('vue-router').Route} to
 * @param {boolean} isTestFlag
 * @returns {undefined|Object} 리다이렉트 대상 또는 undefined
 */
export const checkTestRedirect = (to, isTestFlag) => {
  if (!isTestFlag) return guardSkip()
  const targetPath = '/ml/dataset/detail'
  const targetDatasetName = 'financial_dataset_prepared_copy_1'
  const alreadyThere =
    to.path === targetPath && to.query.datasetName === targetDatasetName
  if (alreadyThere) {
    return guardSkip()
  }
  return guardHandled({
    path: targetPath,
    query: { datasetName: targetDatasetName },
  })
}

/**
 * URL 쿼리 파라미터로 전달된 토큰 처리
 * @param {import('vue-router').Route} to
 * @returns {Promise<Object>|undefined} 리다이렉트 대상 또는 undefined
 */
export const handleUrlTokenLogin = (to) => {
  if (!to.query.accessToken) return guardSkip()
  const accessToken = String(to.query.accessToken)
  const refreshToken = to.query.refreshToken
    ? String(to.query.refreshToken)
    : null
  const tokenStore = useTokenStore(pinia)
  tokenStore.setToken({
    accessToken,
    refreshToken,
    refreshTime: null,
  })
  const userStore = useUserStore(pinia)
  const clusterStore = useClusterStore(pinia)

  const axiosWithToken = AXIOS.create({
    timeout: 20000,
    headers: {
      Authorization: `Bearer ${to.query.accessToken}`,
    },
  })

  const userinfoUrl = '/api/v1/member/me'
  const clusterUrl = '/api/v1/cluster'

  const handleError = (label, error) => {
    console.error(label, error)
    return guardHandled({ path: '/signin' })
  }

  return axiosWithToken
    .get(userinfoUrl)
    .then((userinfoResponse) => {
      const expiry = new Date().getTime() + LOGIN_EXPIRY_DURATION_MS
      userStore.setUserinfo(userinfoResponse.data)
      userStore.setLoginExpiry(expiry)
      const rawRole = userinfoResponse?.data?.role
      const normalizedRole =
        rawRole && String(rawRole).toUpperCase().startsWith('ROLE_')
          ? String(rawRole).toUpperCase().replace('ROLE_', '')
          : rawRole
      const isAdmin = normalizedRole === 'ADMIN'

      return axiosWithToken
        .get(clusterUrl)
        .then((clusterResponse) => {
          if (to.query.cluster) {
            const matched = clusterResponse.data.find(
              (x) => String(x.id) === to.query.cluster
            )
            if (matched) {
              clusterStore.setClusterInfo(matched)
              return guardHandled({
                path: isAdmin ? '/dashboard' : '/cluster',
                query: {},
              })
            }
            return guardHandled({ path: '/cluster', query: {} })
          }
          clusterStore.removeClusterInfo()
          return guardHandled({
            path: isAdmin ? '/clusters' : '/cluster',
            query: {},
          })
        })
        .catch((error) => handleError('클러스터 정보 조회 실패:', error))
    })
    .catch((error) => {
      alert('사용자 정보 조회 실패:', error)
      return handleError('사용자 정보 조회 실패:', error)
    })
}

/**
 * 토큰/만료/권한 검사
 * @param {import('vue-router').Route} to
 * @param {import('vue-router').Route} from
 * @returns {undefined|Object|boolean} 리다이렉트 대상 또는 undefined
 */
export const checkAuth = (to, from) => {
  const tokenStore = useTokenStore(pinia)
  const token = tokenStore.accessToken
  const userStore = useUserStore(pinia)
  let loginExpiry = userStore.loginExpiry
  // 만료 정보가 없지만 토큰이 있다면, 한 번은 갱신 기회를 부여
  if (token && !loginExpiry) {
    loginExpiry = new Date().getTime() + LOGIN_EXPIRY_DURATION_MS
    userStore.setLoginExpiry(loginExpiry)
  }
  const isExpired = loginExpiry
    ? loginExpiry < new Date().getTime()
    : true
  const role = userStore.userinfo ? userStore.userinfo.role : null
  let normalizedRole = role ? String(role).toUpperCase() : null
  if (normalizedRole && normalizedRole.startsWith('ROLE_')) {
    normalizedRole = normalizedRole.replace('ROLE_', '')
  }
  if (!normalizedRole && token) {
    const payload = decodeJwtPayload(token)
    const derivedRole = payload?.auth || payload?.role || payload?.roles?.[0]
    if (derivedRole) {
      normalizedRole = String(derivedRole).toUpperCase()
      if (normalizedRole.startsWith('ROLE_')) {
        normalizedRole = normalizedRole.replace('ROLE_', '')
      }
      const current = userStore.userinfo || {}
      userStore.setUserinfo({ ...current, role: derivedRole })
    }
  }

  if (isExpired) {
    tokenStore.removeToken()
    if (from.fullPath !== '/signin' && to.fullPath === '/signin') {
      return guardSkip()
    }
    if (to.fullPath === '/signup') return guardSkip()
    return guardHandled({ path: '/signin' })
  }

  if (from.fullPath !== '/signin' && to.fullPath === '/signin') {
    if (token && role) {
      // 이미 로그인된 상태면 만료 시간을 갱신하고 홈으로 리다이렉트
      const renewed = new Date().getTime() + LOGIN_EXPIRY_DURATION_MS
      userStore.setLoginExpiry(renewed)
      return guardHandled({ path: '/' })
    }
    return guardSkip()
  }

  if (!token) {
    if (to.path === '/signup') return guardSkip()
    return guardHandled({ path: '/signin' })
  }

  const renewed = new Date().getTime() + LOGIN_EXPIRY_DURATION_MS
  userStore.setLoginExpiry(renewed)

  if (to.fullPath === '/') {
    return guardSkip()
  }

  const authRoles = Array.isArray(to.meta?.auth)
    ? to.meta.auth.map((r) => String(r).toUpperCase())
    : []
  if (authRoles.length) {
    // 역할 정보가 없으면 로그인 화면으로 돌려보냄
    if (!normalizedRole) {
      return guardHandled({ path: '/signin' })
    }
    // 권한 불일치 시 에러 페이지로 이동
    if (!authRoles.includes(normalizedRole)) {
      return guardHandled({ path: '/error' })
    }
  }

  const isPro = to.meta.isPro
  if (isPro) {
    const frontendType = getFrontendType()
    if (frontendType !== 'PRO') {
      return guardHandled({ path: '/error' })
    }
  }

  return guardSkip()
}

/**
 * 루트 접근 시 사용자 권한/클러스터에 따른 리다이렉트
 * @param {import('vue-router').Route} to
 * @returns {undefined|Object} 리다이렉트 대상 또는 undefined
 */
export const checkRootRedirect = (to) => {
  if (to.fullPath !== '/') return guardSkip()
  const userStore = useUserStore(pinia)
  const clusterStore = useClusterStore(pinia)
  const role = userStore.userinfo ? userStore.userinfo.role : null
  const normalizedRole =
    role && String(role).toUpperCase().startsWith('ROLE_')
      ? String(role).toUpperCase().replace('ROLE_', '')
      : role
  let cluster = clusterStore.clusterInfo
  if (!cluster) {
    const restoredCluster = restoreClusterFromStorage()
    if (restoredCluster) {
      clusterStore.setClusterInfo(restoredCluster)
      cluster = restoredCluster
    }
  }
  const isAdmin = normalizedRole === 'ADMIN'
  if (!cluster) {
    return guardHandled({ path: isAdmin ? '/clusters' : '/cluster' })
  }
  return guardHandled({ path: isAdmin ? '/dashboard' : '/cluster' })
}

/**
 * 클러스터가 필요한 페이지 검사
 * @param {import('vue-router').Route} to
 * @returns {Promise<Object|boolean>|undefined} 리다이렉트 대상 또는 undefined
 */
export const checkCluster = (to) => {
  const clusterRequiredPaths = [
    '/home',
    '/dashboard',
    '/ml',
    '/container',
    '/training',
    '/volume',
    '/workflow',
    '/management',
    '/image',
    '/buildImage',
  ]
  const needsCluster = clusterRequiredPaths.some((path) =>
    to.path.startsWith(path)
  )
  if (!needsCluster) return guardSkip()
  const clusterStore = useClusterStore(pinia)
  let cluster = clusterStore?.clusterInfo
  if (!cluster) {
    const restoredCluster = restoreClusterFromStorage()
    if (restoredCluster) {
      clusterStore.setClusterInfo(restoredCluster)
      cluster = restoredCluster
    }
  }
  return cluster ? true : { path: '/cluster' }
}
